import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo, useState, useEffect } from 'react';
import { Spinner } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { DataReportsContext } from '../../../contexts/data_reports/data_reports.context';
import { usePaginatedCubeQuery } from '../../hooks/use_paginated_cube_query';
import { useServerSideCubeQuery } from '../../hooks/use_server_side_cube_query';
import { getQueryParams } from '../../utils/data_reports.utils';
import { buildDefaultSortOrder, normalizeDefaultSort, withSortTiebreakers } from '../../utils/order.utils';
import { ChartContent } from '../report_chart/report_chart_content.component';
/**
 * Determines if a component is a reference (new pattern) or embedded config (legacy)
 */
const isComponentRef = (component) => {
    return !component.type && !component.query; // Refs only have id, title, and layout
};
export const CompositeComponent = ({ component, chartQueryFilters, chartPresetFilters, filterParams, studyId, isPublic, cardId, forceFresh = false, refreshNonce = 0, }) => {
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    // Composite components sit inside a study report; format decision comes from
    // the shared context (`showFullTimestamp` = report has `rid` in URL). SSP-4116.
    const { libRequestsParams, showFullTimestamp } = useContext(DataReportsContext);
    const [sortingConfig, setSortingConfig] = useState({});
    const [isLoadingResultSet, setIsLoadingResultSet] = useState(true);
    // Server-side pagination state for table charts
    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: 5,
    });
    // Create a unique component ID that includes the parent card ID
    const componentKey = `${cardId}-${component.id}`;
    const componentLibraryResourceId = isComponentRef(component) ? component.libraryResourceId : undefined;
    // Get component chart data (only for new pattern - component references)
    const getComponentChartData = async () => {
        const params = componentLibraryResourceId
            ? { id: componentLibraryResourceId, ...(isPublic && { public: true }), ...libRequestsParams }
            : { key: component.id };
        // check entity lib first, and then public
        const { data: response } = await libraryClient.get('/v1/reporting_chart', {
            params,
        });
        if (!componentLibraryResourceId && response.data.length === 0) {
            const { data: publicResponse } = await libraryClient.get('/v1/reporting_chart', {
                params: { ...params, public: true },
            });
            return publicResponse.data[0]?.data;
        }
        return response.data[0]?.data;
    };
    const shouldFetchChartData = isComponentRef(component) && !isPublic;
    const { data: componentResource, isLoading: isLoadingComponentData } = useQuery({
        queryKey: ['data_reports', studyId, 'charts_list', component.id],
        queryFn: getComponentChartData,
        enabled: shouldFetchChartData,
    });
    // Convert component to Chart format for ChartContent
    const componentData = useMemo(() => {
        // Handle new pattern: component reference
        if (isComponentRef(component)) {
            if (isPublic) {
                // For public dashboards, we can't fetch by ID, so this shouldn't happen
                console.warn('Component references not supported in public dashboards');
                return null;
            }
            if (!componentResource) {
                return null;
            }
            // Use fetched chart data
            return componentResource;
        }
        // Handle legacy pattern: embedded component config
        const legacyComponent = component;
        return {
            id: legacyComponent.id,
            title: legacyComponent.title || '',
            type: legacyComponent.type === 'chart' ? legacyComponent.chartType || 'bar' : legacyComponent.type,
            query: legacyComponent.query || {},
            pivotConfig: legacyComponent.pivotConfig,
            style: legacyComponent.style,
            cardInfo: legacyComponent.cardInfo,
            tooltipConfig: legacyComponent.tooltipConfig,
            bubbleConfig: legacyComponent.bubbleConfig,
        };
    }, [component, componentResource, isPublic]);
    // Builder-configured default sort (SSP-3919), re-derived rather than trusted
    // from the persisted query.order so the study view matches the Library
    // (SSP-4500).
    const configuredDefaultSort = useMemo(() => normalizeDefaultSort(componentData?.defaultSort), [componentData?.defaultSort]);
    // Transform query based on sorting configuration. Append the component's
    // other columns as tiebreakers so cube LIMIT/OFFSET stays deterministic
    // when the sorted column has ties (SSP-3889).
    const transformedQuery = useMemo(() => {
        if (!componentData?.query) {
            return undefined;
        }
        const tiebreakerKeys = [...(componentData.query.dimensions || []), ...(componentData.query.measures || [])];
        const defaultSortOrder = configuredDefaultSort && buildDefaultSortOrder(configuredDefaultSort, tiebreakerKeys);
        return {
            ...componentData.query,
            ...{ renewQuery: true, total: true },
            ...(Object.keys(sortingConfig).length > 0
                ? { order: withSortTiebreakers(sortingConfig, tiebreakerKeys) }
                : defaultSortOrder && { order: defaultSortOrder }),
        };
    }, [componentData, sortingConfig, configuredDefaultSort]);
    const queryParams = useMemo(() => getQueryParams(transformedQuery, filterParams, chartQueryFilters[componentKey] || {}, chartPresetFilters[componentKey] || []), [transformedQuery, filterParams, chartQueryFilters, chartPresetFilters, componentKey]);
    const isTableChart = componentData?.type === 'table';
    // Use server-side pagination for table charts, full pagination for other chart types
    const paginatedResult = usePaginatedCubeQuery(queryParams, {
        skip: !componentData || !isLoadingResultSet || isTableChart,
        disablePreAggs: componentData?.disablePreAggs,
        forceFresh,
        refreshNonce,
    });
    const serverSideResult = useServerSideCubeQuery(queryParams, {
        skip: !componentData || !isTableChart,
        disablePreAggs: componentData?.disablePreAggs,
        forceFresh,
        refreshNonce,
        pagination: paginationState,
    });
    // Select the appropriate result based on chart type
    const resultSets = isTableChart
        ? serverSideResult.resultSet
            ? [serverSideResult.resultSet]
            : null
        : paginatedResult.resultSets;
    const isLoading = isTableChart ? serverSideResult.isLoading : paginatedResult.isLoading;
    const error = isTableChart ? serverSideResult.error : paginatedResult.error;
    const totalRows = serverSideResult.total;
    // Reset pagination when filters/sorting change (only for table charts)
    useEffect(() => {
        if (isTableChart) {
            setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
        }
    }, [queryParams, isTableChart]);
    useEffect(() => {
        if (!isTableChart) {
            setIsLoadingResultSet(true);
        }
    }, [queryParams, isTableChart, refreshNonce]);
    useEffect(() => {
        if (!isTableChart && (resultSets || error)) {
            setIsLoadingResultSet(false);
        }
    }, [resultSets, error, isTableChart]);
    // Handler for pagination changes from TableChart
    const handlePaginationChange = (newPagination) => {
        setPaginationState(newPagination);
    };
    // Show loading state
    if (isLoadingComponentData || !componentData) {
        return (_jsx("div", { className: 'flex items-center justify-center size-full min-h-[200px]', children: _jsx(Spinner, { size: 'xl' }) }));
    }
    // Show error state
    if (error) {
        return (_jsxs("div", { className: 'flex flex-col items-center justify-center size-full min-h-[200px] p-4', children: [_jsx("div", { className: 'text-red-600 text-center', children: "Failed to load component" }), _jsx("div", { className: 'text-sm text-gray-500 text-center mt-1', children: error.message })] }));
    }
    return (_jsx("div", { className: 'h-full w-full flex items-center justify-center', children: _jsx("div", { className: 'w-full', children: _jsx(ChartContent, { isLoading: isLoading, chartError: error, resultSets: resultSets || undefined, chartType: componentData.type, pivotConfig: componentData.pivotConfig, style: componentData.style, setSortingConfig: setSortingConfig, defaultSort: configuredDefaultSort, tooltipConfig: componentData.tooltipConfig, bubbleConfig: componentData.bubbleConfig, showAnnotations: componentData.showAnnotations, disablePreAggs: componentData.disablePreAggs, roleBasedVisibility: componentData.roleBasedVisibility, serverSidePagination: isTableChart
                    ? {
                        total: totalRows,
                        paginationState,
                        onPaginationChange: handlePaginationChange,
                    }
                    : undefined, showFullTimestamp: showFullTimestamp }) }) }));
};
